import React, { useMemo } from 'react';

/**
 * FleurDeVie - Sacred Geometry Gamification Component
 * 
 * This component renders a dynamic Flower of Life visualization that grows
 * based on the user's progress (points and level).
 */
const FleurDeVie = ({ 
  points = 0, 
  level = 1, 
  size = 200,
  showStats = true,
  animated = true 
}) => {
  // Calculate flower growth based on level
  const flowerData = useMemo(() => {
    // Petals increase with level
    const petalCount = Math.min(12, 3 + level);
    
    // Colors evolve with level
    const colorPalettes = [
      { primary: '#FFB6C1', secondary: '#FF69B4', glow: '#FF1493' }, // Level 1-2: Pink (Seed)
      { primary: '#DDA0DD', secondary: '#BA55D3', glow: '#9932CC' }, // Level 3-4: Purple (Sprout)
      { primary: '#87CEEB', secondary: '#4169E1', glow: '#0000FF' }, // Level 5-6: Blue (Growth)
      { primary: '#98FB98', secondary: '#32CD32', glow: '#228B22' }, // Level 7-8: Green (Bloom)
      { primary: '#FFD700', secondary: '#FFA500', glow: '#FF8C00' }, // Level 9-10: Gold (Full Bloom)
      { primary: '#E6E6FA', secondary: '#9370DB', glow: '#8A2BE2' }, // Level 11+: Violet (Transcendent)
    ];
    
    const colorIndex = Math.min(Math.floor((level - 1) / 2), colorPalettes.length - 1);
    const colors = colorPalettes[colorIndex];
    
    // Stage names
    const stages = [
      { name: 'Graine', emoji: '🌱', minLevel: 1 },
      { name: 'Pousse', emoji: '🌿', minLevel: 3 },
      { name: 'Bourgeon', emoji: '🌸', minLevel: 5 },
      { name: 'Floraison', emoji: '🌺', minLevel: 7 },
      { name: 'Épanouissement', emoji: '🌻', minLevel: 9 },
      { name: 'Transcendance', emoji: '✨', minLevel: 11 },
    ];
    
    const currentStage = stages.reduce((acc, stage) => 
      level >= stage.minLevel ? stage : acc, stages[0]);
    
    // Progress to next level (0-100)
    // For very high levels (like admin level 99), show 100% as they've mastered everything
    const isMaxLevel = level >= 99;
    const pointsForCurrentLevel = (level - 1) * 100;
    const pointsForNextLevel = level * 100;
    const progressToNext = isMaxLevel 
      ? 100 
      : Math.max(0, Math.min(100, ((points - pointsForCurrentLevel) / (pointsForNextLevel - pointsForCurrentLevel)) * 100));
    
    return {
      petalCount,
      colors,
      currentStage,
      progressToNext,
      innerCircles: Math.min(3, Math.floor(level / 3) + 1),
      outerRings: Math.min(2, Math.floor(level / 5)),
      glowIntensity: Math.min(1, level / 10),
    };
  }, [points, level]);

  const { petalCount, colors, currentStage, progressToNext, innerCircles, outerRings, glowIntensity } = flowerData;
  
  const center = size / 2;
  const baseRadius = size / 6;
  
  // Generate petal positions using sacred geometry
  const petals = useMemo(() => {
    const result = [];
    for (let i = 0; i < petalCount; i++) {
      const angle = (i * 360) / petalCount;
      const radian = (angle * Math.PI) / 180;
      const x = center + Math.cos(radian) * baseRadius;
      const y = center + Math.sin(radian) * baseRadius;
      result.push({ x, y, angle, delay: i * 0.1 });
    }
    return result;
  }, [petalCount, center, baseRadius]);

  // Generate inner circles (Seed of Life pattern)
  const innerCircleElements = useMemo(() => {
    const circles = [];
    if (innerCircles >= 1) {
      // Center circle
      circles.push({ cx: center, cy: center, r: baseRadius * 0.6, delay: 0 });
    }
    if (innerCircles >= 2) {
      // Six surrounding circles
      for (let i = 0; i < 6; i++) {
        const angle = (i * 60 * Math.PI) / 180;
        circles.push({
          cx: center + Math.cos(angle) * baseRadius * 0.5,
          cy: center + Math.sin(angle) * baseRadius * 0.5,
          r: baseRadius * 0.3,
          delay: 0.2 + i * 0.05
        });
      }
    }
    return circles;
  }, [innerCircles, center, baseRadius]);

  return (
    <div className="flex flex-col items-center" data-testid="fleur-de-vie-container">
      {/* SVG Flower Visualization */}
      <div className="relative" style={{ width: size, height: size }}>
        <svg 
          width={size} 
          height={size} 
          viewBox={`0 0 ${size} ${size}`}
          className="overflow-visible"
          data-testid="fleur-de-vie-svg"
        >
          {/* Definitions for gradients and filters */}
          <defs>
            {/* Main gradient */}
            <radialGradient id="flowerGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={colors.primary} stopOpacity="0.9" />
              <stop offset="70%" stopColor={colors.secondary} stopOpacity="0.7" />
              <stop offset="100%" stopColor={colors.glow} stopOpacity="0.3" />
            </radialGradient>
            
            {/* Glow filter */}
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation={3 * glowIntensity} result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            
            {/* Petal gradient */}
            <linearGradient id="petalGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.primary} stopOpacity="0.8" />
              <stop offset="100%" stopColor={colors.secondary} stopOpacity="0.5" />
            </linearGradient>
          </defs>
          
          {/* Outer glow ring (appears at higher levels) */}
          {outerRings > 0 && (
            <circle
              cx={center}
              cy={center}
              r={baseRadius * 2.5}
              fill="none"
              stroke={colors.glow}
              strokeWidth="1"
              strokeOpacity={0.3 * glowIntensity}
              className={animated ? 'animate-pulse' : ''}
              style={{ animationDuration: '3s' }}
            />
          )}
          
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={baseRadius * 2}
            fill="url(#flowerGradient)"
            opacity="0.2"
            filter="url(#glow)"
          />
          
          {/* Petals (Flower of Life pattern) */}
          <g filter="url(#glow)">
            {petals.map((petal, i) => (
              <g 
                key={i}
                className={animated ? 'animate-pulse' : ''}
                style={{ 
                  transformOrigin: `${petal.x}px ${petal.y}px`,
                  animationDelay: `${petal.delay}s`,
                  animationDuration: '2s'
                }}
              >
                <circle
                  cx={petal.x}
                  cy={petal.y}
                  r={baseRadius}
                  fill="none"
                  stroke="url(#petalGradient)"
                  strokeWidth="2"
                  opacity={0.6 + (0.4 * (i / petalCount))}
                />
              </g>
            ))}
          </g>
          
          {/* Inner circles (Seed of Life) */}
          {innerCircleElements.map((circle, i) => (
            <circle
              key={`inner-${i}`}
              cx={circle.cx}
              cy={circle.cy}
              r={circle.r}
              fill={i === 0 ? colors.secondary : 'none'}
              fillOpacity={i === 0 ? 0.4 : 0}
              stroke={colors.primary}
              strokeWidth={i === 0 ? 3 : 1}
              className={animated && i === 0 ? 'animate-pulse' : ''}
              style={{ animationDuration: '2.5s', animationDelay: `${circle.delay}s` }}
            />
          ))}
          
          {/* Center sacred symbol */}
          <circle
            cx={center}
            cy={center}
            r={baseRadius * 0.25}
            fill={colors.glow}
            filter="url(#glow)"
            className={animated ? 'animate-pulse' : ''}
            style={{ animationDuration: '1.5s' }}
          />
          
          {/* Progress ring */}
          <circle
            cx={center}
            cy={center}
            r={baseRadius * 1.8}
            fill="none"
            stroke={colors.secondary}
            strokeWidth="3"
            strokeOpacity="0.2"
          />
          <circle
            cx={center}
            cy={center}
            r={baseRadius * 1.8}
            fill="none"
            stroke={colors.glow}
            strokeWidth="3"
            strokeDasharray={`${2 * Math.PI * baseRadius * 1.8}`}
            strokeDashoffset={`${2 * Math.PI * baseRadius * 1.8 * (1 - progressToNext / 100)}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
            className="transition-all duration-1000"
          />
        </svg>
        
        {/* Level badge */}
        <div 
          className="absolute top-0 right-0 w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg"
          data-testid="fleur-level-badge"
        >
          {level}
        </div>
      </div>
      
      {/* Stats display */}
      {showStats && (
        <div className="mt-4 text-center" data-testid="fleur-stats">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-2xl">{currentStage.emoji}</span>
            <span className="text-white font-bold text-lg">{currentStage.name}</span>
          </div>
          
          <div className="flex items-center justify-center gap-4 text-sm">
            <div className="text-pink-200">
              <span className="text-yellow-400 font-bold">{points}</span> points
            </div>
            <div className="text-pink-200">
              Niveau <span className="text-cyan-400 font-bold">{level}</span>
            </div>
          </div>
          
          <div className="mt-2 text-xs text-pink-300">
            {level >= 99 
              ? "Niveau maximum atteint! 🏆" 
              : `${progressToNext.toFixed(0)}% vers le niveau ${level + 1}`
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default FleurDeVie;
